<template>
  <div class="broker-login-page">
    <!-- Language selector at top-right -->
    <div class="language-selector-container">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <button v-bind="attrs" v-on="on" class="language-pill" elevation="0">
            <Flag :code="currentLanguageFlag" :squared="false" class="flag" />
            <span class="lang-code">{{ currentLanguage.toUpperCase() }}</span>
            <v-icon class="chevron" color="white" size="16"
              >fa-regular fa-chevron-down</v-icon
            >
          </button>
        </template>
        <v-list dense class="language-list">
          <v-list-item
            v-for="lang in availableLanguages"
            :key="lang.code"
            @click="changeLanguage(lang.code)"
          >
            <v-list-item-content>
              <v-list-item-title class="language-item-title">
                <Flag :code="lang.flag" :squared="false" class="mr-2" />
                {{ lang.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="currentLanguage === lang.code">
              <v-icon color="primary" small>fa-regular fa-check</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="login-grid">
      <div class="left-card">
        <div class="left-content">
          <div class="form-content">
            <!-- Logo and Title -->
            <div class="left-header">
              <img
                class="brand-logo"
                src="@/assets/brokers/Logo-swiset.png"
                alt="SWISET"
              />
              <div class="brand-sep">—</div>
              <div class="brand-title">
                {{ $t("broker.login.brokerAdmin") }}
              </div>
            </div>
            <div class="welcome-block">
              <h1 class="welcome-title">{{ $t("broker.login.welcome") }}</h1>
              <p class="welcome-subtitle">
                {{ $t("broker.login.credentials") }}
              </p>
            </div>

            <!-- Login Form -->
            <v-form
              v-model="formValid"
              @submit.prevent="handleLogin"
              class="login-form"
            >
              <div class="field-group">
                <label class="field-label">{{
                  $t("broker.login.email")
                }}</label>
                <v-text-field
                  v-model.trim="credentials.username"
                  placeholder="name@broker.com"
                  :rules="usernameRules"
                  outlined
                  class="auth-input"
                  @keyup.enter="handleLogin"
                />
              </div>

              <div class="field-group">
                <div class="field-row">
                  <label class="field-label-password">{{
                    $t("broker.login.password")
                  }}</label>
                  <a
                    target="_blank"
                    href="https://swiset.atlassian.net/servicedesk/customer/portal/1/group/-1"
                    class="forgot-link"
                    >{{ $t("broker.login.forgotPassword") }}</a
                  >
                </div>

                <v-text-field
                  v-model.trim="credentials.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="************"
                  outlined
                  :rules="passwordRules"
                  class="auth-input"
                  @keyup.enter="handleLogin"
                >
                  <template v-slot:append>
                    <i
                      @click="showPassword = !showPassword"
                      :class="
                        showPassword
                          ? 'fa-regular fa-eye'
                          : 'fa-regular fa-eye-slash'
                      "
                      class="password-eye-icon"
                    />
                  </template>
                </v-text-field>
              </div>

              <v-checkbox
                v-model="rememberMe"
                :label="$t('broker.login.rememberMe')"
                color="primary"
                :ripple="false"
                class="remember-check"
                hide-details
              />
              <v-btn
                type="submit"
                color="primary"
                block
                large
                :loading="loading"
                :disabled="!canLogin || loading"
                class="login-btn"
                @click="handleLogin"
              >
                {{ $t("broker.login.loginButton") }}
              </v-btn>
            </v-form>
          </div>
        </div>
      </div>

      <!-- Right Panel - Image/Branding -->

      <div class="right-content">
        <div class="right-inner">
          <v-carousel
            class="awards-carousel"
            height="240"
            cycle
            interval="3000"
            :show-arrows="false"
            hide-delimiters
            hide-delimiter-background
          >
            <v-carousel-item v-for="(s, i) in slides" :key="i">
              <div class="triple-slide">
                <!-- left preview -->
                <img
                  class="slide-img slide-img--side"
                  :src="slides[prevIndex(i)].src"
                  :alt="slides[prevIndex(i)].alt"
                />

                <!-- center main -->
                <img
                  class="slide-img slide-img--main"
                  :src="slides[i].src"
                  :alt="slides[i].alt"
                />

                <!-- right preview -->
                <img
                  class="slide-img slide-img--side"
                  :src="slides[nextIndex(i)].src"
                  :alt="slides[nextIndex(i)].alt"
                />
              </div>
            </v-carousel-item>
          </v-carousel>
          <p class="text-broker">
            {{ $t("broker.login.message") }}
          </p>
        </div>
        <div class="right-watermark">
          <img src="@/assets/brokers/Logo-swiset.png" alt="SWISET" />
        </div>
      </div>
    </div>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" :timeout="5000" color="error" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import authService from "../services/auth.service";
import { getAvailableLocales, setLocale, getLocale } from "../i18n";

export default {
  name: "BrokerLogin",
  data() {
    return {
      loginForm: null,
      loading: false,
      formValid: false,
      showPassword: false,
      rememberMe: false,
      showError: false,
      errorMessage: "",
      currentLanguage: getLocale(),
      availableLanguages: getAvailableLocales(),
      credentials: {
        username: "", // Pre-filled for testing, remove in production
        password: "", // Pre-filled for testing, remove in production
      },
      usernameRules: [
        (v) => !!v || "Email address is required",
        (v) => v.length >= 3 || "Username must be at least 3 characters",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => v.length >= 6 || "Password must be at least 6 characters",
      ],
      slides: [
        {
          src: require("@/assets/brokers/Award-Guinness-World-Record.png"),
          alt: "Prize 1",
        },
        {
          src: require("@/assets/brokers/Award-Best-Fintech-Solution.png"),
          alt: "Prize 2",
        },
        {
          src: require("@/assets/brokers/Award-Best-Trading-Competition-Provider.png"),
          alt: "Prize 3",
        },
        {
          src: require("@/assets/brokers/Award-Best-Trading-Tools.png"),
          alt: "Prize 4",
        },
        {
          src: require("@/assets/brokers/Award-Top-100-Global-Startups.png"),
          alt: "Prize 5",
        },
      ],
    };
  },
  mounted() {
    const remember = localStorage.getItem("broker_remember");
    const username = localStorage.getItem("broker_username");

    if (remember === "true" && username) {
      this.rememberMe = true;
      this.credentials.username = username;
    }
  },

  computed: {
    currentLanguageFlag() {
      const lang = this.availableLanguages.find(
        (l) => l.code === this.currentLanguage,
      );
      return lang ? lang.flag : "US";
    },
    canLogin() {
      return (
        !!this.credentials.username?.trim() &&
        !!this.credentials.password?.trim()
      );
    },
  },
  methods: {
    async handleLogin() {
      if (!this.formValid) return;

      this.loading = true;
      this.showError = false;

      const mapErrorToMessage = (errorKey) => {
        if (!errorKey) return this.$t("broker.login.errors.generic");

        if (
          typeof errorKey === "string" &&
          errorKey.startsWith("error.http.")
        ) {
          const code = errorKey.replace("error.http.", "");
          const i18nKey = `broker.login.errors.http${code}`;

          // si no existe traducción para ese code, cae al genérico
          return this.$te(i18nKey)
            ? this.$t(i18nKey)
            : this.$t("broker.login.errors.generic");
        }

        return this.$t("broker.login.errors.generic");
      };

      try {
        const response = await authService.login(
          this.credentials.username,
          this.credentials.password,
        );

        if (response && response.success) {
          if (this.rememberMe) {
            localStorage.setItem("broker_remember", "true");
            localStorage.setItem("broker_username", this.credentials.username);
          } else {
            localStorage.removeItem("broker_remember");
            localStorage.removeItem("broker_username");
          }
          if (
            this.$root.$children[0] &&
            this.$root.$children[0].checkAuthState
          ) {
            await this.$root.$children[0].checkAuthState();
          }

          await this.$router.push("/");
        } else {
          this.errorMessage = mapErrorToMessage(response?.error);
          this.showError = true;
        }
      } catch (error) {
        const backendKey =
          error?.response?.data?.error || error?.response?.data?.message;
        this.errorMessage = mapErrorToMessage(backendKey);
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
    changeLanguage(code) {
      setLocale(code);
      this.currentLanguage = code;
    },
    prevIndex(i) {
      return (i - 1 + this.slides.length) % this.slides.length;
    },
    nextIndex(i) {
      return (i + 1) % this.slides.length;
    },
  },
};
</script>

<style lang="scss" scoped>
.broker-login-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background-image: url("~@/assets/brokers/Background-Login.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.login-grid {
  width: 100%;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 0;
  align-items: stretch;
}

.left-card {
  height: 100%;
  background: #fff !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22) !important;
  border-top-right-radius: 2rem;
  border-bottom-right-radius: 2rem;
}
.left-content {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 44px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 52px 56px;
  font-family: "Avenir LT Std", "Avenir", sans-serif;
}

.left-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18%;
}

.brand-logo {
  height: 26px;
  width: auto;
}

.brand-title {
  font-family: "Aeonik", sans-serif !important;
  font-size: 26px;
  font-weight: 400;
  text-transform: capitalize;
  color: #005931 !important;
}
.brand-sep {
  color: #005931 !important;
}


.welcome-title {
  color: #005931 !important;
  font-size: 34px;
  font-weight: 700;
  margin: 0 0 10px 0;
}

.welcome-subtitle {
  color: #3b3b3b;
  font-size: 14px;
  margin: 0;
  max-width: 420px;
}

.field-label {
  display: block;
  font-size: 12px;
  color: #3b3b3b;
  margin-bottom: 8px;
}
.field-label-password {
  display: block;
  font-size: 12px;
  color: #3b3b3b;
}

.password-eye-icon {
  font-size: 18px !important;
  color: #757575 !important;
  cursor: pointer !important;
  transition: color 0.2s ease !important;
  line-height: 1 !important;

  &:hover {
    color: #424242 !important;
  }
}

.field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.form-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.login-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 20px;
}

.forgot-link {
  font-size: 12px;
  color: rgba(59, 59, 59, 0.55);
  text-decoration: none;
}

.forgot-link:hover {
  text-decoration: underline;
}

::v-deep .auth-input.v-text-field--outlined .v-input__slot {
  border-radius: 4px !important;
}

::v-deep .login-form .v-messages__message {
  margin: 0;
  padding: 0;
}

::v-deep .login-form .v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

::v-deep .login-form .v-label {
  font-size: 14px;
}

::v-deep .v-input--selection-controls__ripple {
  width: 20px;
  height: 20px;
}

::v-deep .v-btn.login-btn.v-btn--disabled {
  background-color: #C3FFE3!important;
}

.login-btn {
  margin-top: 50px;
  height: 44px !important;
  border-radius: 6px !important;
  flex: 0 0 auto;
}

.right-content {
  position: relative;
  height: 100%;
  padding: 44px 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.awards-carousel {
  width: 100%;
  max-width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px; /* más compacto */
  overflow: hidden; /* evita que se desborde */
}

/* image layout */
.triple-slide {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.slide-img {
  object-fit: contain;
  filter: brightness(0) invert(1);
  object-fit: contain;
  max-width: 100%;
  transition: all 0.6s ease;
}

.slide-img--main {
  width: 55%;
  max-height: 180px;
  opacity: 0.98;
  transform: scale(1);
}

.slide-img--side {
  width: 20%;
  max-height: 120px;
  opacity: 0.45;
  transform: scale(0.95);
}

.text-broker {
  margin-top: 12px;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  color: rgba(255, 255, 255, 0.85) !important;
}

.right-watermark {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
}
.right-watermark img {
  width: 100%;
  height: auto;
  object-fit: contain;
  opacity: 0.9;
}
.right-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: center;
}

// Lenguage selector

.language-selector-container {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
}

.language-pill {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 4px 10px !important;
  border-radius: 10px !important;
  border: 2px solid rgba(255, 255, 255, 0.55) !important;
  background: rgba(0, 0, 0, 0.12) !important;
  color: rgba(255, 255, 255, 0.85) !important;
  text-transform: none !important;
  min-width: 130px;

  &::before,
  &::after {
    display: none !important;
  }
}

.flag {
  width: 44px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
}

.lang-code {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.chevron {
  margin-left: auto;
  opacity: 0.85;
}

.language-list {
  min-width: 220px;
}

.language-item-title {
  display: flex;
  align-items: center;
}

/* Responsive: en móvil solo card */
@media (max-width: 960px) {
  .login-grid {
    grid-template-columns: 1fr;
  }

  .right-content {
    display: none !important;
  }

  .left-card {
    width: 100%;
    border-radius: 0; /* opcional: en mobile suele verse mejor */
  }

  .broker-login-page {
    min-height: 100vh;
    min-height: 100dvh; /* mejor en mobile */
  }

  .left-content {
    min-height: 100vh;
    min-height: 100dvh;
    height: auto !important;
    justify-content: flex-start !important;
    padding: 84px 22px 28px; /* top para el pill, lateral y bottom */
  }

  .form-content {
    width: 100%;
  }

  .brand-title {
    font-size: 16px;
  }

  .welcome-block {
    margin-top: 62px;
  }

  .field-group {
    margin-top: 16px;
  }

  /* Mantén el botón pegado al form, no al fondo */
  .login-btn {
    margin-top: 22px !important;
  }

  /* Si tu botón se estaba yendo abajo por un margin grande */
  .login-btn {
    height: 44px !important;
  }

  /* Ajusta selector de idioma para que no choque */
  .language-selector-container {
    top: 16px;
    right: 16px;
  }

  .language-pill {
    min-width: 110px;
    padding: 6px 10px !important;
  }

  .lang-code {
    font-size: 16px;
  }

  .flag {
    width: 32px;
    height: 20px;
  }
}
</style>
